import React from 'react';
import { Helmet } from 'react-helmet';

import { useI18n } from 'common/hooks';

type Meta = {
  title: string;
  description: string;
};

type Views = {
  root: Meta;
  trade: Meta;
  asset: Meta;
  derivatives: Meta;
  derivative: Meta;
  exchange: Meta;
  referral: Meta;
};

type Props = {
  view: keyof Views;
  additionalData?: {
    baseCurrencyShortName?: string;
    quotedCurrencyShortName?: string;
    quotedCurrencyFullName?: string;
  };
};

export const MetaTags: React.FC<React.PropsWithChildren<Props>> = ({ view, additionalData }) => {
  const { translate } = useI18n();
  const {
    quotedCurrencyFullName = '',
    quotedCurrencyShortName = '',
    baseCurrencyShortName = '',
  } = additionalData || {};

  const views: Views = {
    root: {
      title: translate.toText('mobile:meta.root.title'),
      description: translate.toText('mobile:meta.root.description'),
    },
    trade: {
      title: translate.toText('mobile:meta.trade.title'),
      description: translate.toText('mobile:meta.trade.description'),
    },
    asset: {
      title: translate
        .toText('mobile:meta.asset.title')
        .replace('{baseCurrencyShortName}', baseCurrencyShortName)
        .replace('{quotedCurrencyShortName}', quotedCurrencyShortName),
      description: translate
        .toText('mobile:meta.asset.description')
        .replace('{baseCurrencyShortName}', baseCurrencyShortName)
        .replace('{quotedCurrencyShortName}', quotedCurrencyShortName)
        .replace('{quotedCurrencyFullName}', quotedCurrencyFullName),
    },
    derivatives: {
      title: translate.toText('mobile:meta.derivatives.title'),
      description: translate.toText('mobile:meta.derivatives.description'),
    },
    derivative: {
      title: translate
        .toText('mobile:meta.derivatives.title')
        .replace('{baseCurrencyShortName}', baseCurrencyShortName)
        .replace('{quotedCurrencyShortName}', quotedCurrencyShortName),
      description: translate
        .toText('mobile:meta.derivatives.description')
        .replace('{baseCurrencyShortName}', baseCurrencyShortName)
        .replace('{quotedCurrencyShortName}', quotedCurrencyShortName)
        .replace('{quotedCurrencyFullName}', quotedCurrencyFullName),
    },
    exchange: {
      title:
        quotedCurrencyShortName && baseCurrencyShortName
          ? translate
              .toText('mobile:meta.exchange.title')
              .replace('{baseCurrencyShortName}', baseCurrencyShortName)
              .replace('{quotedCurrencyShortName}', quotedCurrencyShortName)
          : translate.toText('mobile:meta.exchange_without_currencies.title'),
      description:
        quotedCurrencyShortName && baseCurrencyShortName
          ? translate
              .toText('mobile:meta.exchange.description')
              .replace('{baseCurrencyShortName}', baseCurrencyShortName)
              .replace('{quotedCurrencyShortName}', quotedCurrencyShortName)
          : translate.toText('mobile:meta.exchange_without_currencies.description'),
    },
    referral: {
      title: translate.toText('mobile:meta.referral.title'),
      description: translate.toText('mobile:meta.referral.description'),
    },
  };

  return (
    <Helmet encodeSpecialCharacters>
      <meta name='title' content={views[view].title} />
      <meta name='description' content={views[view].description} />
    </Helmet>
  );
};
